import React, { useState, useEffect, useRef } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { detectProvider } from '../../lib/mailProviders';
import {
  Mail, Send, Inbox, CheckCircle2, Loader2, ArrowRight, ArrowLeft, X,
  Zap, Server, Sparkles, AlertTriangle, ChevronDown, PartyPopper,
} from 'lucide-react';

const STEPS = ['Sending', 'Verify', 'Inbox', 'Test'];

const SEND_METHODS = [
  { id: 'gmail', name: 'Gmail / Google', icon: Mail, desc: 'One-click Google sign-in', note: "Google verification is in progress. You may see a \u201CGoogle hasn\u2019t verified this app\u201D screen \u2014 this is expected and safe. Tap \u201CAdvanced\u201D \u2192 \u201CContinue to Lockene\u201D to connect." },
  { id: 'sendgrid', name: 'SendGrid', icon: Zap, desc: 'API key + verified sender' },
  { id: 'smtp', name: 'Other (SMTP)', icon: Server, desc: 'Any mailbox — Zoho, Outlook, custom' },
];

export default function ConnectMailWizard({ open, onClose, onComplete }) {
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState(null);
  const [fields, setFields] = useState({});
  const [detected, setDetected] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [imapStatus, setImapStatus] = useState(null);
  const [imapError, setImapError] = useState('');
  // B5: once the user hand-edits a server host/port, MX auto-detect must NOT
  // clobber their manual value on later keystrokes.
  const hostTouched = useRef(false);

  useEffect(() => {
    if (open) {
      setStep(1); setMethod(null); setFields({}); setDetected(null);
      setShowAdvanced(false); setBusy(false); setImapStatus(null); setImapError('');
      hostTouched.current = false;
    }
  }, [open]);

  const set = (k, v) => setFields((f) => ({ ...f, [k]: v }));
  // Marks the server fields as user-edited so MX detect leaves them alone.
  const setHost = (k, v) => { hostTouched.current = true; set(k, v); };

  // Custom domain → ask the backend to resolve the REAL provider via MX records
  // (e.g. lockene.info → Zoho) instead of relying on the mail.<domain> guess.
  useEffect(() => {
    const email = fields.email || '';
    if (!detected?.custom || !email.includes('@') || email.split('@')[1]?.indexOf('.') === -1) return;
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/api/tenant/integrations/mail/detect', { params: { email } });
        const d = res.data || {};
        if (d.detected && d.smtp_host) {
          // B5: MX result wins unless the user manually edited the host fields.
          setFields((f) => ({
            ...f,
            smtp_host: hostTouched.current ? f.smtp_host : d.smtp_host,
            smtp_port: hostTouched.current ? f.smtp_port : d.smtp_port,
            imap_host: hostTouched.current ? f.imap_host : (d.imap_host || f.imap_host),
            imap_port: hostTouched.current ? f.imap_port : (d.imap_port || f.imap_port),
          }));
          setDetected({ key: 'mx', name: d.provider, note: d.note, custom: false, viaMx: true });
        } else if (d.note) {
          setDetected((prev) => (prev?.custom ? { ...prev, note: d.note, unresolved: true } : prev));
        }
      } catch { /* keep local guess */ }
    }, 600);
    return () => clearTimeout(t);
  }, [fields.email, detected?.custom]);

  const onEmailChange = (email) => {
    const d = detectProvider(email);
    setDetected(d);
    setFields((f) => {
      const next = { ...f, email };
      if (d) {
        // Known providers overwrite host/port (so switching zoho→outlook updates
        // cleanly); custom domains only fill empties so user edits are preserved.
        // B5: once the user hand-edits any host field, never clobber it here.
        const overwrite = !d.custom && !hostTouched.current;
        next.smtp_host = (overwrite || !f.smtp_host) ? d.smtp_host : f.smtp_host;
        next.smtp_port = (overwrite || !f.smtp_port) ? d.smtp_port : f.smtp_port;
        next.imap_host = (overwrite || !f.imap_host) ? d.imap_host : f.imap_host;
        next.imap_port = (overwrite || !f.imap_port) ? d.imap_port : f.imap_port;
      }
      return next;
    });
  };

  const chooseMethod = async (m) => {
    setMethod(m);
    if (m === 'gmail') {
      setBusy(true);
      try {
        const res = await api.get('/api/oauth/gmail/connect');
        const url = res.data?.authorization_url || res.data?.auth_url || res.data?.url;
        if (url) { window.location.href = url; return; }
        toast.error('Could not start Google sign-in');
      } catch (e) {
        toast.error('Could not start Google sign-in', { description: e?.response?.data?.detail || '' });
      } finally { setBusy(false); }
      return;
    }
    setStep(2);
  };

  const connectSending = async () => {
    setBusy(true);
    try {
      if (method === 'sendgrid') {
        if (!fields.api_key || !fields.sender_email) { toast.error('Enter your SendGrid API key and verified sender'); setBusy(false); return; }
        await api.post('/api/tenant/integrations/sendgrid', {
          provider: 'sendgrid', is_enabled: true,
          api_key: fields.api_key, sender_email: fields.sender_email,
        });
      } else {
        if (!fields.email || !fields.smtp_password || !fields.smtp_host) { toast.error('Enter your email and app password'); setBusy(false); return; }
        await api.post('/api/tenant/integrations/smtp', {
          provider: 'smtp', is_enabled: true,
          smtp_host: fields.smtp_host, smtp_port: parseInt(fields.smtp_port) || 465,
          smtp_username: fields.email, smtp_password: fields.smtp_password,
          smtp_from_email: fields.email, smtp_from_name: fields.from_name || '',
          imap_host: fields.imap_host, imap_port: parseInt(fields.imap_port) || 993,
        });
      }
      toast.success('Sending identity connected & verified ✓');
      setStep(3);
      if (method === 'smtp') await refreshImap();
    } catch (e) {
      toast.error('Could not verify — check credentials', { description: e?.response?.data?.detail || 'Verification failed' });
    } finally { setBusy(false); }
  };

  const refreshImap = async () => {
    try {
      const res = await api.get('/api/tenant/integrations/smtp');
      setImapStatus(res.data?.imap_status || null);
      setImapError(res.data?.imap_last_error || '');
    } catch { /* ignore */ }
  };

  const testInbound = async () => {
    setBusy(true);
    try {
      const res = await api.post('/api/tenant/integrations/smtp/test');
      await refreshImap();
      res.data?.success ? toast.success('Inbound (IMAP) connection OK ✓') : toast.error(res.data?.message || 'IMAP test failed');
    } catch (e) {
      toast.error('IMAP test failed', { description: e?.response?.data?.detail || '' });
    } finally { setBusy(false); }
  };

  const saveImapEdit = async () => {
    setBusy(true);
    try {
      await api.post('/api/tenant/integrations/smtp', {
        provider: 'smtp', is_enabled: true,
        smtp_host: fields.smtp_host, smtp_port: parseInt(fields.smtp_port) || 465,
        smtp_username: fields.email, smtp_password: fields.smtp_password,
        smtp_from_email: fields.email,
        imap_host: fields.imap_host, imap_port: parseInt(fields.imap_port) || 993,
      });
      await refreshImap();
      toast.success('Inbox settings saved');
    } catch (e) {
      toast.error('Save failed', { description: e?.response?.data?.detail || '' });
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const res = await api.post('/api/tenant/integrations/email/send-test', {});
      res.data?.success !== false
        ? toast.success(res.data?.message || 'Test email sent ✓')
        : toast.error(res.data?.message || 'Test failed');
    } catch (e) {
      toast.error('Test send failed', { description: e?.response?.data?.detail || '' });
    } finally { setBusy(false); }
  };

  const finish = () => { onComplete?.(); onClose?.(); };

  const isDns = (imapError || '').toLowerCase().match(/name or service not known|getaddrinfo|nodename|-2]/);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="connect-mail-wizard">
        {/* Header + progress */}
        <div className="px-6 pt-5 pb-4 border-b border-border">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center"><Mail className="h-4 w-4 text-primary" /></div>
            <DialogTitle className="text-lg font-semibold">Connect your mail</DialogTitle>
            <DialogDescription className="sr-only">Guided setup to connect your email sending identity and inbound reply capture.</DialogDescription>
          </div>
          <div className="flex items-center gap-1.5">
            {STEPS.map((label, i) => {
              const n = i + 1;
              const done = n < step, active = n === step;
              return (
                <React.Fragment key={label}>
                  <div className={`flex items-center gap-1.5 text-[11px] font-medium ${active ? 'text-primary' : done ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                    <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] border ${active ? 'border-primary bg-primary/10' : done ? 'border-emerald-500 bg-emerald-500/10' : 'border-border'}`}>
                      {done ? <CheckCircle2 className="h-3 w-3" /> : n}
                    </span>
                    <span className="hidden sm:inline">{label}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`flex-1 h-px ${done ? 'bg-emerald-500/40' : 'bg-border'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
          {/* STEP 1 — choose sending method */}
          {step === 1 && (
            <div className="space-y-3" data-testid="wizard-step-sending">
              <p className="text-sm text-muted-foreground">How should your emails go out? Pick the mailbox you send from.</p>
              {SEND_METHODS.map((m) => (
                <button key={m.id} onClick={() => chooseMethod(m.id)} disabled={busy} data-testid={`wizard-method-${m.id}`}
                  className="w-full flex items-center gap-3 rounded-lg border border-border p-3 text-left hover:border-primary/40 hover:bg-primary/5 transition-colors disabled:opacity-60">
                  <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center shrink-0"><m.icon className="h-4 w-4" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium flex items-center gap-2">{m.name}</p>
                    <p className="text-xs text-muted-foreground">{m.desc}</p>
                    {m.note && <p className="text-[11px] text-amber-600 dark:text-amber-500 mt-1 leading-snug" data-testid={`wizard-method-${m.id}-note`}>{m.note}</p>}
                  </div>
                  {busy && m.id === method ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4 text-muted-foreground" />}
                </button>
              ))}
            </div>
          )}

          {/* STEP 2 — credentials */}
          {step === 2 && method === 'smtp' && (
            <div className="space-y-3" data-testid="wizard-step-smtp">
              <p className="text-sm text-muted-foreground">Enter your email and app password — we auto-detect your server settings.</p>
              <div>
                <label className="text-xs font-medium">Email address</label>
                <Input value={fields.email || ''} onChange={(e) => onEmailChange(e.target.value)} placeholder="you@company.com" data-testid="wizard-smtp-email" />
              </div>
              {detected && !detected.unresolved && (
                <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-500/5 border border-emerald-500/20 rounded-md px-2.5 py-1.5" data-testid="wizard-detected-banner">
                  <Sparkles className="h-3.5 w-3.5 shrink-0" /> Detected <span className="font-medium">{detected.name}</span>{detected.viaMx ? ' (via your domain\u2019s mail records)' : ''} — server settings auto-filled.
                </div>
              )}
              {detected?.unresolved && (
                <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-500/5 border border-amber-500/20 rounded-md px-2.5 py-1.5" data-testid="wizard-unresolved-banner">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> We couldn't auto-detect your mail servers.
                </div>
              )}
              {detected?.note && <p className="text-[11px] text-muted-foreground">{detected.note}</p>}
              <div>
                <label className="text-xs font-medium">App password</label>
                <Input type="password" value={fields.smtp_password || ''} onChange={(e) => set('smtp_password', e.target.value)} placeholder="App-specific password" data-testid="wizard-smtp-password" />
              </div>
              <button onClick={() => setShowAdvanced((s) => !s)} className="text-[11px] text-muted-foreground flex items-center gap-1 hover:text-foreground" data-testid="wizard-advanced-toggle">
                <ChevronDown className={`h-3 w-3 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} /> Advanced server settings
              </button>
              {showAdvanced && (
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-[10px] text-muted-foreground">SMTP host</label><Input value={fields.smtp_host || ''} onChange={(e) => setHost('smtp_host', e.target.value)} data-testid="wizard-smtp-host" /></div>
                  <div><label className="text-[10px] text-muted-foreground">SMTP port</label><Input value={fields.smtp_port || ''} onChange={(e) => setHost('smtp_port', e.target.value)} data-testid="wizard-smtp-port" /></div>
                  <div><label className="text-[10px] text-muted-foreground">IMAP host</label><Input value={fields.imap_host || ''} onChange={(e) => setHost('imap_host', e.target.value)} data-testid="wizard-imap-host-adv" /></div>
                  <div><label className="text-[10px] text-muted-foreground">IMAP port</label><Input value={fields.imap_port || ''} onChange={(e) => setHost('imap_port', e.target.value)} data-testid="wizard-imap-port-adv" /></div>
                </div>
              )}
            </div>
          )}
          {step === 2 && method === 'sendgrid' && (
            <div className="space-y-3" data-testid="wizard-step-sendgrid">
              <p className="text-sm text-muted-foreground">Paste your SendGrid API key and the sender email you verified in SendGrid.</p>
              <div><label className="text-xs font-medium">API key</label><Input type="password" value={fields.api_key || ''} onChange={(e) => set('api_key', e.target.value)} placeholder="SG.xxxxx" data-testid="wizard-sg-key" /></div>
              <div><label className="text-xs font-medium">Verified sender email</label><Input value={fields.sender_email || ''} onChange={(e) => set('sender_email', e.target.value)} placeholder="you@company.com" data-testid="wizard-sg-sender" /></div>
              <p className="text-[11px] text-muted-foreground">Must be a Verified Sender (or on an authenticated domain) in SendGrid.</p>
            </div>
          )}

          {/* STEP 3 — inbound */}
          {step === 3 && (
            <div className="space-y-3" data-testid="wizard-step-inbox">
              <p className="text-sm text-muted-foreground flex items-center gap-1.5"><Inbox className="h-4 w-4" /> Capture replies so cadences auto-pause and thread.</p>
              {method === 'smtp' ? (
                <>
                  {imapStatus === 'connected' ? (
                    <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-500/5 border border-emerald-500/20 rounded-md px-3 py-2" data-testid="wizard-imap-ok">
                      <CheckCircle2 className="h-4 w-4" /> Inbox connected — replies will be captured automatically.
                    </div>
                  ) : imapStatus === 'needs_attention' ? (
                    <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs" data-testid="wizard-imap-error">
                      <p className="text-amber-600 font-medium flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />{isDns ? `IMAP host not found (${fields.imap_host})` : 'Couldn\'t connect to your inbox'}</p>
                      <p className="text-muted-foreground mt-0.5">{isDns ? 'Check the IMAP server address below and save.' : 'Check the IMAP host and app password below.'}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Testing your inbox connection…</p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-[10px] text-muted-foreground">IMAP host</label><Input value={fields.imap_host || ''} onChange={(e) => setHost('imap_host', e.target.value)} data-testid="wizard-imap-host" /></div>
                    <div><label className="text-[10px] text-muted-foreground">IMAP port</label><Input value={fields.imap_port || ''} onChange={(e) => setHost('imap_port', e.target.value)} /></div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={saveImapEdit} disabled={busy} data-testid="wizard-imap-save">Save inbox settings</Button>
                    <Button size="sm" variant="outline" onClick={testInbound} disabled={busy} data-testid="wizard-imap-test">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}Test connection</Button>
                  </div>
                </>
              ) : (
                <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs space-y-1">
                  <p className="font-medium">Reply capture for {method === 'sendgrid' ? 'SendGrid' : 'Gmail'}</p>
                  <p className="text-muted-foreground">Gmail replies are captured automatically after connecting. For SendGrid, set up Inbound Parse (forwarding) — or add a mailbox via IMAP under <span className="font-medium">Advanced integrations</span> on the previous page. You can do this anytime; it won't block sending.</p>
                </div>
              )}
            </div>
          )}

          {/* STEP 4 — test + done */}
          {step === 4 && (
            <div className="space-y-4 text-center py-2" data-testid="wizard-step-test">
              <div className="h-12 w-12 rounded-full bg-emerald-500/10 mx-auto flex items-center justify-center"><PartyPopper className="h-6 w-6 text-emerald-600" /></div>
              <div>
                <p className="text-base font-semibold">You're live 🎉</p>
                <p className="text-sm text-muted-foreground">Send yourself a test email to confirm everything works.</p>
              </div>
              <Button onClick={sendTest} disabled={busy} data-testid="wizard-send-test">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Send test email
              </Button>
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="px-6 py-3 border-t border-border flex items-center justify-between">
          {step > 1 && step < 4 ? (
            <Button variant="ghost" size="sm" onClick={() => setStep((s) => s - 1)} data-testid="wizard-back"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="wizard-close"><X className="h-4 w-4 mr-1" />Close</Button>
            {step === 2 && (
              <Button size="sm" onClick={connectSending} disabled={busy} data-testid="wizard-connect-verify">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}Connect &amp; verify
              </Button>
            )}
            {step === 3 && <Button size="sm" onClick={() => setStep(4)} data-testid="wizard-next-test">Continue<ArrowRight className="h-4 w-4 ml-1" /></Button>}
            {step === 4 && <Button size="sm" onClick={finish} data-testid="wizard-finish">Finish</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
